import { useState } from 'react';
import { api, ApiFailure, type Session } from '../api';
import { Notice } from './Panels';

export function Auth({ onSignedIn, initialMode, onBack }: { onSignedIn: (session: Session) => void; initialMode?: 'login' | 'signup'; onBack?: () => void }) {
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode ?? 'login');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setFailure(null);

    try {
      const session =
        mode === 'signup'
          ? await api.signup({ email, name, password })
          : await api.login({ email, password });
      onSignedIn(session);
    } catch (error) {
      setFailure(
        error instanceof ApiFailure
          ? error
          : new ApiFailure('Could not sign you in.', 'Try again.', 0),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        {onBack && (
          <button
            className="ghost tiny"
            onClick={onBack}
            style={{ position: 'absolute', top: 16, right: 16 }}
          >
            ← Back
          </button>
        )}

        <div className="auth-brand">
          <span className="mark" aria-hidden />
          DataVantage
        </div>

        <h1>{mode === 'signup' ? 'Create your account' : 'Welcome back'}</h1>
        <p className="muted" style={{ marginTop: 0, marginBottom: 20 }}>
          {mode === 'signup'
            ? 'Start tracking revenue attribution in under 2 minutes.'
            : 'Sign in to continue to your dashboard.'}
        </p>

        {failure !== null && (
          <div>
            <Notice tone="error" message={failure.message} remedy={failure.remedy} />
            {failure.status === 409 && mode === 'signup' && (
              <button
                className="linklike"
                style={{ marginTop: 8 }}
                onClick={() => {
                  setFailure(null);
                  setMode('login');
                }}
              >
                Go to sign in →
              </button>
            )}
          </div>
        )}

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {mode === 'signup' && (
            <label className="field">
              <span>Full name</span>
              <input
                type="text"
                value={name}
                required
                autoComplete="name"
                placeholder="Jane Doe"
                onChange={(e) => setName(e.target.value)}
              />
            </label>
          )}

          <label className="field">
            <span>Email</span>
            <input
              type="email"
              value={email}
              required
              autoComplete="email"
              placeholder="you@company.com"
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label className="field">
            <span>Password</span>
            <input
              type="password"
              value={password}
              required
              minLength={mode === 'signup' ? 10 : 1}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              placeholder={mode === 'signup' ? 'At least 10 characters' : 'Enter your password'}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          <button className="primary" type="submit" disabled={busy} style={{ marginTop: 6, padding: '10px 16px', fontSize: 14 }}>
            {busy ? <span className="spinner" /> : mode === 'signup' ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <div className="auth-switch">
          {mode === 'signup' ? (
            <>
              Already have an account?{' '}
              <button className="linklike" onClick={() => setMode('login')}>
                Sign in
              </button>
            </>
          ) : (
            <>
              New to DataVantage?{' '}
              <button className="linklike" onClick={() => setMode('signup')}>
                Create an account
              </button>
            </>
          )}
        </div>
      </div>

      <aside className="auth-aside">
        <div className="auth-aside-content">
          <h2>Revenue-first analytics</h2>
          <p>Connect marketing activity directly to money.</p>
          <ul>
            <li>Track visitors, sessions, and touchpoints automatically</li>
            <li>7 attribution models including Markov Chains & Shapley Value</li>
            <li>Revenue, MRR, LTV, CAC, ROAS — all computed, none invented</li>
            <li>Connects Google Search Console, BigQuery, Sheets & Meta Ads</li>
          </ul>
        </div>
      </aside>
    </div>
  );
}
